import { useEffect, useState } from "react";
import { PayloadType, DeviceTypeId, PayloadTypeId } from "../../types";
import { Button, Grid } from "@mui/material";
import HexRawDataParse from "../HexRawDataParse/index";
import CustomSnackbar from "../Common/CustomSnackbar";
import { usePayloadParser } from "./hooks/usePayloadParser";
import PayloadEditor from "./PayloadEditor";
import PayloadParametersTable from "./PayloadParametersTable";
import PayloadTreeView from "./PayloadTreeView";
import ConfirmDialog from "../Common/ConfirmDialog";
import { useSnackbar } from "../Common/hooks/useSnackbar";
import useAuth from "../../hoc/AuthProvider/useAuth";
import { Permissions } from "../../config/permissions";

type Prop = {
  selectedParserConfig: number;
  payloadTypeId: number;
  deviceTypeId: number;
};

const PayloadParse: React.FC<Prop> = ({ selectedParserConfig, payloadTypeId, deviceTypeId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRawItem, setSelectedRawItem] = useState<PayloadType>();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { showSuccess, showError, snackbarProps } = useSnackbar();
  const { havePermission } = useAuth();
  const canSave = havePermission(Permissions.ParserConfig_Update);

  const {
    payloadText,
    payloadData,
    schemaItems,
    setSchemaItems,
    loading,
    isPayloadEdited,
    isExistingData,
    fetchDataAndParse,
    handlePayloadUpload,
    handlePayloadUploadWithValue,
    handlePayloadChange,
    handleRowUpdate,
    handleArrayExpandRowUpdate,
    handleParseRuleSave,
    handleIsDeviceIdChange,
    handleSaveAll,
    resetState,
  } = usePayloadParser(selectedParserConfig, payloadTypeId, deviceTypeId);

  useEffect(() => {
    resetState();
    loadInitialData();
  }, [selectedParserConfig]);

  const loadInitialData = async () => {
    try {
      await fetchDataAndParse();
    } catch (error) {
      showError((error as Error).message || "Veri alınırken hata oluştu");
    }
  };

  const handleParse = async () => {
    try {
      await handlePayloadUpload();
    } catch (error) {
      showError(error as Error);
    }
  };

  const handleEditClick = (row: PayloadType) => {
    setSelectedRawItem(row);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const successMessage = await handleSaveAll();
      showSuccess(successMessage);
    } catch (error) {
      showError((error as Error).message || "Kayıt sırasında hata oluştu");
    }
  };

  return (
    <>
      <Grid container direction={"row"}>
        <PayloadEditor
          payloadText={payloadText}
          isPayloadEdited={isPayloadEdited}
          selectedParserConfig={selectedParserConfig}
          onPayloadChange={handlePayloadChange}
          onParse={handleParse}
          onError={showError}
        />

        <Grid size={{ xs: 12, md: 6 }} sx={{ paddingBottom: 2, paddingLeft: { xs: 0, md: 2 } }}>
          {deviceTypeId === DeviceTypeId.Gateway ? (
            <PayloadTreeView schemaItems={schemaItems} setSchemaItems={setSchemaItems} loading={loading} />
          ) : (
            <PayloadParametersTable
              payloadData={payloadData}
              loading={loading}
              showDeviceId={payloadTypeId === PayloadTypeId.Json && deviceTypeId === DeviceTypeId.Gateway}
              onRowUpdate={handleRowUpdate}
              onIsDeviceIdChange={handleIsDeviceIdChange}
              onEditClick={handleEditClick}
              onArrayExpandRowUpdate={handleArrayExpandRowUpdate}
            />
          )}
        </Grid>

        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: -2,
          }}
        >
          <Button
            size="medium"
            variant="contained"
            sx={{
              px: 4,
              backgroundColor: "var(--color-primary)",
              borderRadius: "6px",
              textTransform: "none",
              "&:hover": { backgroundColor: "var(--color-primary-dark)" },
            }}
            disabled={!canSave || !(payloadText && selectedParserConfig && !isPayloadEdited)}
            onClick={() => setIsConfirmOpen(true)}
          >
            {"Kaydet"}
          </Button>
        </Grid>
      </Grid>

      <HexRawDataParse
        hexData={selectedRawItem?.value?.toString() ?? ""}
        keyValueId={selectedRawItem?.rawDataTypeId ?? 0}
        rules={selectedRawItem?.parseTable ?? []}
        bindingRules={selectedRawItem?.bindingData ?? []}
        open={isModalOpen}
        showDeviceId={payloadTypeId === PayloadTypeId.ByteArray && deviceTypeId === DeviceTypeId.Gateway}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRawItem(undefined);
        }}
        onSave={(rules, bindings) => {
          if (selectedRawItem) {
            handleParseRuleSave(selectedRawItem.key, rules, bindings);
          }
        }}
      />

      <CustomSnackbar {...snackbarProps} />

      <ConfirmDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => {
          setIsConfirmOpen(false);
          handleSave();
        }}
        message="Yeni parser ayarlarını kaydetmek üzeresiniz. Devam etmek istiyor musunuz?"
        confirmText="Kaydet"
        cancelText="Vazgeç"
      />
    </>
  );
};

export default PayloadParse;
